import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ArrowLeftIcon,
  PlusIcon,
  DocumentTextIcon,
  UserIcon,
  BuildingOffice2Icon,
  DocumentCheckIcon,
  IdentificationIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { getEmployeeCompetencies, assignCompetencyToEmployee, updateEmployeeCompetency, removeEmployeeCompetency } from "../../../../api/hse/competency";
import { getCompetencyDefinitions } from "../../../../api/hse/competency";
import { fetchUser } from "../../../../api/users";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency Employee View - Employee competency details
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyEmployeeView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  
  const [employee, setEmployee] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    statuses: [],
    types: [],
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [competencyDefinitions, setCompetencyDefinitions] = useState([]);
  const [editFormData, setEditFormData] = useState({
    competency_definition_id: "",
    issued_at: "",
    expires_at: "",
    certificate_number: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });
  const canViewAll = isAdmin || canManage;

  useEffect(() => {
    if (canViewAll) {
      fetchEmployeeData();
      fetchCompetencyDefinitions();
    }
  }, [id, canViewAll, filters]);

  const fetchCompetencyDefinitions = async () => {
    try {
      const response = await getCompetencyDefinitions({ is_active: true });
      setCompetencyDefinitions(response.competency_definitions || response.definitions || []);
    } catch (err) {
      console.error("Error fetching competency definitions:", err);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      setError(false);
      
      // Fetch employee info
      const userResponse = await fetchUser(id);
      setEmployee({
        id,
        name: userResponse.data?.user?.name || "",
        email: userResponse.data?.user?.email || "",
        department: userResponse.data?.user?.department?.name || "",
      });
      
      // Fetch competencies
      const apiFilters = {
        status: filters.statuses.length > 0 ? filters.statuses[0] : undefined,
        type: filters.types.length > 0 ? filters.types[0] : undefined,
      };
      
      const response = await getEmployeeCompetencies(id, apiFilters);
      setCompetencies(response.competencies || []);
    } catch (err) {
      console.error("Error fetching employee data:", err);
      setError(true);
      toast.error(t("Kunne ikke laste ansattdata"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      statuses: newFilters.statuses || [],
      types: newFilters.types || [],
    });
  };

  // Competency types for filtering
  const competencyTypes = [
    { value: "cv", label: t("CV"), icon: DocumentTextIcon },
    { value: "certificate", label: t("Sertifikat"), icon: DocumentCheckIcon },
    { value: "course", label: t("Kurs"), icon: AcademicCapIcon },
    { value: "license", label: t("Lisens"), icon: IdentificationIcon },
    { value: "other", label: t("Annet"), icon: DocumentTextIcon },
  ];

  // Status options for ModuleFilter
  const statusOptions = [
    { value: "active", label: t("Gyldig"), class: "bg-green-100 text-green-800" },
    { value: "expiring_soon", label: t("Utløper snart"), class: "bg-yellow-100 text-yellow-800" },
    { value: "expired", label: t("Utløpt"), class: "bg-red-100 text-red-800" },
    { value: "permanent", label: t("Permanent"), class: "bg-gray-100 text-gray-800" },
  ];

  // Custom dropdown filter for competency types
  const customDropdownFilters = [
    {
      key: "types",
      label: t("Type"),
      icon: DocumentTextIcon,
      options: competencyTypes.map(type => ({
        value: type.value,
        label: type.label,
        class: "bg-blue-100 text-blue-800",
      })),
    },
  ];

  // If user doesn't have access, redirect
  if (!canViewAll) {
    navigate("/dashboard/hse-ks/competency");
    return null;
  }

  const getStatusBadge = (competency) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    const now = new Date();
    const expiresAt = competency.expires_at ? new Date(competency.expires_at) : null;
    
    if (!expiresAt) {
      return (
        <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          {t("Permanent")}
        </span>
      );
    }
    
    const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          {t("Utløpt")}
        </span>
      );
    } else if (daysUntilExpiry <= 30) {
      return (
        <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
          <ClockIcon className="h-3 w-3 mr-1" />
          {t("Utløper snart")}
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-green-100 text-green-800`}>
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          {t("Gyldig")}
        </span>
      );
    }
  };


  const handleEditClick = (competency) => {
    setSelectedCompetency(competency);
    setEditFormData({
      competency_definition_id: competency.competency_definition_id || "",
      issued_at: competency.issued_at ? competency.issued_at.split('T')[0] : "",
      expires_at: competency.expires_at ? competency.expires_at.split('T')[0] : "",
      certificate_number: competency.certificate_number || "",
      notes: competency.notes || "",
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (competency) => {
    setSelectedCompetency(competency);
    setDeleteDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompetency) return;

    try {
      setSaving(true);
      await updateEmployeeCompetency(id, selectedCompetency.id, editFormData);
      toast.success(t("Kompetanse oppdatert"));
      setEditDialogOpen(false);
      setSelectedCompetency(null);
      fetchEmployeeData();
    } catch (err) {
      console.error("Error updating competency:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.errors?.join(", ") || err.message || t("Kunne ikke oppdatere kompetanse");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCompetency) return;

    try {
      setSaving(true);
      await removeEmployeeCompetency(id, selectedCompetency.id);
      toast.success(t("Kompetanse fjernet"));
      setDeleteDialogOpen(false);
      setSelectedCompetency(null);
      fetchEmployeeData();
    } catch (err) {
      console.error("Error deleting competency:", err);
      const errorMessage = err.response?.data?.error || err.message || t("Kunne ikke fjerne kompetanse");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const stats = {
    total: competencies.length,
    active: competencies.filter(c => {
      const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
      if (!expiresAt) return true;
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 30;
    }).length,
    expiring_soon: competencies.filter(c => {
      const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
      if (!expiresAt) return false;
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 30;
    }).length,
    expired: competencies.filter(c => {
      const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
      if (!expiresAt) return false;
      const daysUntilExpiry = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry < 0;
    }).length,
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste ansattdata")]} />;
  }

  if (!employee) {
    return <ResourceEmpty message={t("Ansatt ikke funnet")} />;
  }

  return (
    <DashboardHeader
      title={t("Ansatt kompetanse")}
      subtitle={employee.name || employee.email}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* Back Button */}
          <div>
            <button
              onClick={() => navigate("/dashboard/hse-ks/competency/employees")}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("Tilbake til ansattoversikt")}
            </button>
          </div>

          {/* Employee Info */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="h-16 w-16 flex-shrink-0">
                  <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
                    <UserIcon className="h-10 w-10 text-indigo-600" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-lg font-medium text-gray-900">{employee.name || employee.email}</h3>
                  {employee.email && employee.name && (
                    <p className="text-sm text-gray-500">{employee.email}</p>
                  )}
                  {employee.department && (
                    <p className="text-sm text-gray-500 flex items-center mt-1">
                      <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                      {employee.department}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AcademicCapIcon className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Totalt")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Gyldige")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.active}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-8 w-8 text-yellow-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Utløper snart")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.expiring_soon}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{t("Utløpte")}</dt>
                      <dd className="text-lg font-semibold text-gray-900">{stats.expired}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i kompetanse...")}
            acceptedStatuses={statusOptions.map(s => s.value)}
            customDropdownFilters={customDropdownFilters}
          />

          {/* Competencies List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("Kompetanser")}</h3>
                {canManage && (
                  <button
                    onClick={() => navigate(`/dashboard/hse-ks/competency/employees/${id}/assign`)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t("Legg til kompetanse")}
                  </button>
                )}
              </div>
              
              {competencies.length === 0 ? (
                <ResourceEmpty message={t("Ingen kompetanser registrert")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Kompetanse")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utstedt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utløper")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {competencies.map((competency) => (
                        <tr key={competency.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              {competency.type === "cv" && <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-3" />}
                              {competency.type === "certificate" && <DocumentCheckIcon className="h-5 w-5 text-green-600 mr-3" />}
                              {competency.type === "course" && <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-3" />}
                              {competency.type === "license" && <IdentificationIcon className="h-5 w-5 text-orange-600 mr-3" />}
                              {(!competency.type || competency.type === "other") && <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3" />}
                              <div>
                                <div className="font-medium text-gray-900">
                                  {competency.competency_definition?.name || competency.name}
                                </div>
                                {competency.competency_definition?.description && (
                                  <div className="text-gray-500 text-xs mt-1">{competency.competency_definition.description}</div>
                                )}
                                {competency.type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                    {competencyTypes.find(t => t.value === competency.type)?.label || competency.type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {getStatusBadge(competency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {competency.issued_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(competency.issued_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {competency.expires_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(competency.expires_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">{t("Permanent")}</span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end space-x-2">
                              {canManage && (
                                <>
                                  <button
                                    onClick={() => handleEditClick(competency)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title={t("Rediger")}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(competency)}
                                    className="text-red-600 hover:text-red-900"
                                    title={t("Slett")}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              <Link
                                to={`/dashboard/hse-ks/competency/definitions/${competency.competency_definition_id || competency.id}`}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                {t("Vis")}
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Competency Dialog */}
      <PMDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedCompetency(null);
        }}
        title={t("Rediger kompetanse")}
        size="2xl"
      >
        <form onSubmit={handleEditSubmit} className="space-y-6">
          {/* Competency Selection */}
          <div>
            <label htmlFor="edit_competency_definition_id" className="block text-sm font-medium text-gray-700">
              {t("Kompetanse")} <span className="text-red-500">*</span>
            </label>
            <select
              id="edit_competency_definition_id"
              required
              value={editFormData.competency_definition_id}
              onChange={(e) => setEditFormData({ ...editFormData, competency_definition_id: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">{t("Velg kompetanse")}</option>
              {competencyDefinitions.map((def) => (
                <option key={def.id} value={def.id}>
                  {def.name} {def.type && `(${def.type})`}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="edit_issued_at" className="block text-sm font-medium text-gray-700">
                {t("Utstedt dato")}
              </label>
              <input
                type="date"
                id="edit_issued_at"
                value={editFormData.issued_at}
                onChange={(e) => setEditFormData({ ...editFormData, issued_at: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="edit_expires_at" className="block text-sm font-medium text-gray-700">
                {t("Utløper dato")}
              </label>
              <input
                type="date"
                id="edit_expires_at"
                value={editFormData.expires_at}
                onChange={(e) => setEditFormData({ ...editFormData, expires_at: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("La stå tomt for permanent kompetanse")}
              </p>
            </div>
          </div>

          {/* Certificate Number */}
          <div>
            <label htmlFor="edit_certificate_number" className="block text-sm font-medium text-gray-700">
              {t("Sertifikatnummer")}
            </label>
            <input
              type="text"
              id="edit_certificate_number"
              value={editFormData.certificate_number}
              onChange={(e) => setEditFormData({ ...editFormData, certificate_number: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder={t("F.eks. CERT-2024-001")}
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="edit_notes" className="block text-sm font-medium text-gray-700">
              {t("Notater")}
            </label>
            <textarea
              id="edit_notes"
              rows={4}
              value={editFormData.notes}
              onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder={t("Legg til notater...")}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setEditDialogOpen(false);
                setSelectedCompetency(null);
              }}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? t("Lagrer...") : t("Lagre")}
            </button>
          </div>
        </form>
      </PMDialog>

      {/* Delete Confirmation Dialog */}
      <PMDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedCompetency(null);
        }}
        title={t("Slett kompetanse")}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {t("Er du sikker på at du vil slette denne kompetansen?")}
          </p>
          {selectedCompetency && (
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm font-medium text-gray-900">
                {selectedCompetency.competency_definition?.name || selectedCompetency.name}
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setDeleteDialogOpen(false);
                setSelectedCompetency(null);
              }}
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              disabled={saving}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {saving ? t("Sletter...") : t("Slett")}
            </button>
          </div>
        </div>
      </PMDialog>
    </DashboardHeader>
  );
};

